/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Grid,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DrawerComponent,
  Form,
  RenderFuncProps,
  Section,
  SelectField,
  SectionDivider,
  AsyncSelect,
  TabPanel,
  TabContainer,
  CheckBoxField,
  TextField,
  RadioField,
} from '@dispatch/web/ui';
import {
  Control,
  FieldErrors,
  FieldValues,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ResourceForm,
  MultiSelectOption,
  Subjects,
  Actions,
  ResourceBulkForm,
  EditEventForm,
  Company,
  ResultStatus,
  LocationFormBulk,
  validateUniqueActiveDays,
} from '@dispatch/shared/types';
import { Ability } from '@casl/ability';
import {
  useCompany,
  useGetResourcesPaginated,
  useTabHandler,
} from '@dispatch/shared/hooks';
import React, { useCallback } from 'react';
import { useDebounce } from 'react-use';
import { StyledFieldSet } from './ResourceDetailsStyles';
import { AttributeAddBulk } from './components/Attributes/AttributeAddBulk';
import { AttributesBulkForm } from './components/Attributes/AttributeFormBulk';
import _, { isEmpty } from 'lodash';
import { ClaimCapacityBulk } from './components/General/ClaimCapacityBulk';
import { TeamsOfficesRolesBulk } from './components/TeamsOfficesRoles/TeamsOfficesRolesBulk';
import { EmptyBulkResourceForm } from './components/Assets/EmptyBulkResourceForm';
import {
  renderAttributesChanges,
  RenderBlockedTimeSection,
  renderClaimOwnerCapacityChanges,
  renderGeneralInformationChanges,
  renderLocationChanges,
  renderTeamOfficesRolesChanges,
  RenderWorkdaySection,
  StyledH3,
  StyledP,
} from './components/BulkResourceComponents';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, endOfDay } from 'date-fns';
import * as yup from 'yup';
import CancelIcon from '@mui/icons-material/Cancel';
import { red } from '@mui/material/colors';
import { statesOptions } from '@dispatch/shared/options';
import { LocationsTabBulk } from './components/Locations/LocationsTabBulk';

export type CompanyOptions = {
  Team: MultiSelectOption[];
  Office: MultiSelectOption[];
  Role: MultiSelectOption[];
};

const checkIfAppend = (value: string) => {
  if (value === 'true') {
    return 'Appending';
  }

  return 'Replacing';
};

type BulkUpdateExclusionZonesProps = {
  navigateToOnClose?: string;
  permissions: Ability<[Actions, Subjects]> | undefined;
  onBulkSave: (values: ResourceBulkForm) => void;
  companyOptions: any;
  company?: Company;
  upsertBulkStatus: ResultStatus;
  setFilterValue: React.Dispatch<React.SetStateAction<any | null>>;
  resourceCount?: number;
};

const tabLabels = [
  'GENERAL',
  'SCHEDULES',
  'LOCATIONS',
  'TEAMS, OFFICES & ROLES',
  'ATTRIBUTES',
  'Confirm',
];

export const allFieldsUndefined = (obj: unknown): boolean => {
  if (_.isNil(obj)) {
    return true;
  }

  if (_.isBoolean(obj)) {
    return false;
  }

  if (_.isObject(obj)) {
    return _.every(obj, (value) => {
      return allFieldsUndefined(value);
    });
  }

  return _.isEmpty(obj);
};

const defaultValues = {
  attributes: [],
  generalInformation: {
    claimCapacity: undefined,
  },
  claimOwner: {
    onSunday: undefined,
    onMonday: undefined,
    onTuesday: undefined,
    onWednesday: undefined,
    onThursday: undefined,
    onFriday: undefined,
    onSaturday: undefined,
    maxWeeklyCapacity: undefined,
  },
  locations: [
    {
      exclusionZonesInformation: {},
    },
  ],
  teamsOfficesRoles: {},
  ids: [],
  isAppendValues: {
    isAppendSchedules: 'false',
    isAppendLocations: 'false',
    isAppendTeamsOfficesRoles: 'false',
    isAppendAttributes: 'false',
  },
};

export const bulkResourceFormSchema = yup.object().shape({
  type: yup.string().required('Type is required'),
  isAppendValues: yup.object({
    isAppendSchedules: yup
      .boolean()
      .transform((_, val: string) => JSON.parse(val)),
    isAppendLocations: yup
      .boolean()
      .transform((_, val: string) => JSON.parse(val)),
    isAppendTeamsOfficesRoles: yup
      .boolean()
      .transform((_, val: string) => JSON.parse(val)),
    isAppendAttributes: yup
      .boolean()
      .transform((_, val: string) => JSON.parse(val)),
  }),
  generalInformation: yup.object({
    claimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    virtualClaimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    complexLossClaimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    efficiency: yup
      .number()
      .min(1, 'Efficiency must be greater than 0')
      .transform((_, val) => Number(val) || null)
      .nullable(),
  }),
  locations: yup
    .array()
    .of(
      yup.object({
        integrationGUID: yup.string(),
        locationInformation: yup.object({
          name: yup.string().required('Name is required'),
          street: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          unit: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          city: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          state: yup.string().required('State is required'),
          zip: yup.string().required('Zip is required'),
          lat: yup.string().nullable(),
          long: yup.string().nullable(),
          isVirtualLocation: yup
            .boolean()
            .transform((_, val: string) => JSON.parse(val)),
        }),
        serviceInformation: yup.object().shape({
          startDate: yup
            .string()
            .nullable()
            .required('Start Date is required')
            .test(
              'validate-year',
              'Start date has an invalid year',
              (value) => {
                if (!value) return true;
                const year = new Date(value).getFullYear();
                return year.toString().startsWith('20');
              }
            ),
          endDate: yup
            .string()
            .nullable()
            .test('validate-year', 'End date has an invalid year', (value) => {
              if (!value) return true;
              const year = new Date(value).getFullYear();
              return year.toString().startsWith('20');
            })
            .test(
              'test-date-range',
              'Start date must be before or equal to the end date',
              (value, context) => {
                if (!value) {
                  return true;
                }
                const startDate = new Date(context.parent.startDate);
                const endDate = new Date(value);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(0, 0, 0, 0);

                return startDate <= endDate;
              }
            )
            .when('isStartingLocation', {
              is: true,
              then: yup
                .string()
                .nullable()
                .required('End Date is required')
                .test(
                  'test-date-range',
                  'Start date must be before or equal to the end date',
                  (value, context) => {
                    if (!value) {
                      return true;
                    }
                    const startDate = new Date(context.parent.startDate);
                    const endDate = new Date(value);
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(0, 0, 0, 0);

                    return startDate <= endDate;
                  }
                ),
            }),
          allowedTravelRadius: yup
            .number()
            .min(0, 'Claim capacity must be non-negative')
            .transform((_, val: string) => Number(val) || 0)
            .required('Travel Radius is required')
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .number()
                .transform(() => 0)
                .nullable(),
            }),
          monday: yup.boolean(),
          tuesday: yup.boolean(),
          wednesday: yup.boolean(),
          thursday: yup.boolean(),
          friday: yup.boolean(),
          saturday: yup.boolean(),
          sunday: yup.boolean(),
          isStartingLocation: yup
            .boolean()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .boolean()
                .transform(() => false)
                .nullable(),
            }), // this is in serviceInformation for yup validation
          isVirtualLocation: yup
            .boolean()
            .transform((_, val: string) => JSON.parse(val)),
          exclusionZones: yup
            .object({
              states: yup.array().of(yup.string()).nullable(),
              counties: yup.array().of(yup.string()).nullable(),
              zipCodes: yup.array().of(yup.string()).nullable(),
            })
            .nullable(),
        }),
      })
    )
    .test(
      'unique-active-days',
      'Two locations cannot be active on the same days',
      validateUniqueActiveDays
    ),
  attributes: yup.array().of(
    yup.object({
      integrationGUID: yup.string(),
      attributeDefinitionIntegrationGUID: yup.string(),
      displayName: yup.string(),
      selectedAttributeValuesVarchars: yup
        .array()
        .of(
          yup.object({
            integrationGUID: yup.string(),
            selectedAttributeDefinitionIntegrationGUID: yup.string(),
            attributeDefinitionValueIntegrationGUID: yup.string(),
            title: yup.string(),
            value: yup.string().required('Please select an attribute'),
            startDate: yup
              .string()
              .nullable()
              .when('endDate', {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                is: (endDate: any) => !!endDate,
                then: yup
                  .string()
                  .nullable()
                  .required('Start date is required if end date is set')
                  .test(
                    'test-date-range',
                    'Start date must be before end date',
                    (value, context) => {
                      if (!context.parent.endDate) {
                        return true;
                      }
                      const startDate = new Date(value?.toString() || '');
                      const endDateRaw = new Date(context.parent.endDate);

                      // Normalize end date to 11:59:59 PM
                      const endDate = endOfDay(endDateRaw);

                      return startDate < endDate;
                    }
                  ),
              }),
            endDate: yup.string().nullable(),
            subAttributes: yup.array().of(
              yup.object({
                displayName: yup.string().required('Field is required'),
                value: yup.string().nullable(),
                startDate: yup.string().required('Start date is required'),
                endDate: yup.string().required('End date is required'),
              })
            ),
          })
        )
        .nullable(),
      selectedAttributeValuesBools: yup
        .object({
          integrationGUID: yup.string(),
          selectedAttributeDefinitionIntegrationGUID: yup.string(),
          attributeDefinitionValueIntegrationGUID: yup.string(),
          title: yup.string(),
          value: yup.boolean(),
          startDate: yup
            .string()
            .nullable()
            .when('endDate', {
              is: true,
              then: yup
                .string()
                .nullable()
                .required('Start date is required if end date is set'),
            }),
          endDate: yup.string().nullable(),
        })
        .nullable(),
    })
  ),
  teamsOfficesRoles: yup.object({
    teams: yup.array().of(yup.string()),
    offices: yup.array().of(yup.string()),
    roles: yup.array().of(yup.string()),
    isContingent: yup.boolean().nullable(),
    contingentCompany: yup.string().nullable().when('isContingent', {
      is: true,
      then: yup.string().required(),
    }),
  }),
  addSelectedAttributeGUID: yup.string().nullable(),
  claimOwner: yup.object({
    onMonday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onTuesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onWednesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onThursday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onFriday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onSaturday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onSunday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    maxWeeklyCapacity: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative')
      .test(
        'test-max-weekly-capacity',
        'Max Weekly Capacity must be less than or equal to the sum of daily capacities',
        (value, context) => {
          if (value === null || value === undefined) {
            return true;
          }

          const totalDailyCapacities = Object.keys(context.parent)
            .filter((key) => key !== 'maxWeeklyCapacity')
            .reduce((sum, key) => sum + context.parent[key], 0);
          const validation =
            context.parent.maxWeeklyCapacity <= totalDailyCapacities;

          return validation;
        }
      ),
  }),
});

export function BulkUpdateResource({
  navigateToOnClose = '/resources',
  permissions,
  onBulkSave,
  companyOptions,
  company,
  upsertBulkStatus,
  setFilterValue,
  resourceCount,
}: BulkUpdateExclusionZonesProps) {
  const { currentTab, setTab } = useTabHandler();
  const { efficiencyOptions } = useCompany();
  const hasUpsertPermissions =
    permissions?.can?.('upsert', 'resource') || false;

  const navigate = useNavigate();

  const methods = useForm<ResourceBulkForm>({
    resolver: yupResolver(bulkResourceFormSchema),
    defaultValues: { ...defaultValues },
  });

  const {
    control: resourceFormControl,
    formState: { errors: resourceFormErrors, isDirty: resourceFormIsDirty },
    handleSubmit: resourceHandleSubmit,
    getValues: resourceGetValues,
    watch: resourceWatch,
    reset: resourceReset,
    setValue: resourceSetValue,
    setError: resourceSetError,
    trigger,
  } = methods;

  const getFilterValue = (type?: string) => {
    switch (type) {
      case 'Team':
        return 'teamIds';
      case 'Office':
        return 'officeIds';
      case 'Group':
        return 'groupIds';
      default:
        return;
    }
  };
  const watchedIds = resourceWatch('ids');
  const watchedType = resourceWatch('type');
  React.useEffect(() => {
    if (watchedIds && watchedIds.length > 0) {
      const filterType = getFilterValue(watchedType);
      if (filterType) {
        setFilterValue({
          [filterType]: filterType === 'officeIds' ? [watchedIds] : watchedIds,
        });
      }
    }
  }, [watchedIds, watchedType, setFilterValue]);

  const resourceFormData = React.useMemo(() => {
    return {
      attributes: [],
    };
  }, [company]);

  React.useEffect(() => {
    if (!resourceFormIsDirty) {
      resourceReset(resourceFormData);
    }
  }, [resourceFormData, resourceFormIsDirty, resourceReset]);

  const handleBulkSave = () => {
    resourceHandleSubmit(onBulkSave)();
  };

  const currentType: any = resourceWatch('type');

  React.useEffect(() => {
    resourceSetValue('ids', []);
  }, [currentType]);

  const [resourceInput, setResourceInput] = React.useState<string>('');
  const [resourceDebounce, setResourceDebounce] = React.useState<string>('');

  useDebounce(
    () => {
      setResourceDebounce(resourceInput);
    },
    1000,
    [resourceInput]
  );

  const filteredResources = useGetResourcesPaginated({
    filters: {
      name: resourceDebounce,
      pageSize: 25, //search only first 25
    },
    enabled: !!resourceDebounce,
  });

  const resourceOptions = React.useMemo(() => {
    return (filteredResources?.data?.map((r) => ({
      value: r.integrationGUID,
      title: r.name,
    })) || []) as MultiSelectOption[];
  }, [filteredResources]);

  const companyOptionsSelection = React.useMemo(() => {
    return companyOptions[currentType];
  }, [currentType]);

  const renderIdField = React.useCallback(
    (type: string) => {
      switch (type) {
        case 'Resources':
          return (
            <Grid item xs={12}>
              <AsyncSelect
                id="ids"
                name="ids"
                label={currentType}
                control={resourceFormControl as any}
                readOnly={!hasUpsertPermissions}
                multiple
                errors={resourceFormErrors}
                options={resourceOptions}
                setInput={setResourceInput}
              />
            </Grid>
          );
        case 'Team':
        case 'Group':
          return (
            <Grid item xs={12}>
              <SelectField
                id="ids"
                name="ids"
                multiple
                label={currentType}
                control={resourceFormControl as any}
                errors={resourceFormErrors}
                readOnly={!hasUpsertPermissions}
                options={companyOptionsSelection || []}
              />
            </Grid>
          );
        default:
          return (
            <Grid item xs={12}>
              <SelectField
                id="ids"
                name="ids"
                label={currentType}
                control={resourceFormControl as any}
                errors={resourceFormErrors}
                readOnly={!hasUpsertPermissions}
                options={companyOptionsSelection || []}
              />
            </Grid>
          );
      }
    },
    [
      resourceFormControl,
      hasUpsertPermissions,
      resourceFormErrors,
      resourceOptions,
      currentType,
      companyOptionsSelection,
    ]
  );

  const hasUpsertSchedulesPermissions = permissions?.can?.(
    'upsert',
    'resource-schedules'
  );
  const hasUpsertEventsPermissions = permissions?.can?.(
    'upsert',
    'resource-events'
  );

  const [openEditSchedule, setOpenEditSchedule] = React.useState(false);

  const handleOpenEditSchedule = () => {
    setIsEdit(false);
    setOpenEditSchedule(true);
  };

  const handleCloseEditSchedule = () => {
    setOpenEditSchedule(false);
  };

  const [openEditEvent, setOpenEditEvent] = React.useState(false);

  const handleOpenEditEvent = () => {
    setIsEdit(false);
    setOpenEditEvent(true);
  };
  const handleCloseEditEvent = () => {
    setOpenEditEvent(false);
  };

  const [isEdit, setIsEdit] = React.useState(false);
  const handleRemove = useCallback(
    (index: number, type: 'events' | 'schedules') => {
      const currentEvents = resourceWatch(type);
      const updatedEvents = currentEvents.filter(
        (event: EditEventForm, i: number) => i !== index
      );
      resourceSetValue(type, updatedEvents);
    },
    [resourceSetValue, resourceWatch]
  );

  const renderAddedEvents = useCallback(
    (events: any, type: 'events' | 'schedules') => {
      return events?.map((event: EditEventForm, index: number) => {
        const allDaysFalse = _.every(event?.days, (day) => day === false);

        return (
          <Card key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Button variant="text" onClick={() => handleRemove(index, type)}>
                <CancelIcon
                  sx={{
                    color: 'red',
                  }}
                />
              </Button>
            </div>
            <CardContent>
              <Stack
                sx={{
                  width: 'fit-content',
                  height: 'fit-content',
                  left: 0,
                  top: '50%',
                }}
              >
                {event?.name && <StyledP>Name: {event.name}</StyledP>}
                <StyledP>
                  Start Date: {format(event.startDateTime, 'MM/dd/yyyy')}
                </StyledP>
                <StyledP>
                  End Date: {format(event.endDateTime, 'MM/dd/yyyy')}
                </StyledP>
                <StyledP>Timezone: {event.timeZoneId}</StyledP>
                {event.category && (
                  <StyledP>Category: {event.category}</StyledP>
                )}
                {event.repeat && (
                  <StyledP>Repeat For: {event.repeatFor}</StyledP>
                )}
                {event.repeat && !allDaysFalse && (
                  <>
                    <StyledP>Days</StyledP>
                    <ul style={{ margin: 0 }}>
                      {Object.entries(event?.days).map(([key, value]) => {
                        return value ? (
                          <li style={{ padding: 0, margin: 0 }}>{key}</li>
                        ) : (
                          ''
                        );
                      })}
                    </ul>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      });
    },
    [handleRemove]
  );

  const renderGeneral = React.useCallback(
    (
      currentTab: number,
      control: Control<FieldValues>,
      errors: FieldErrors,
      index: number
    ) => {
      return (
        <TabPanel value={currentTab} index={index}>
          <div
            style={{
              padding: '10px 24px',
            }}
          >
            <Section title="General Information">
              <Section>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      id="commuteTimeInMin"
                      name="generalInformation.commuteTimeInMin"
                      label="Allowable Commute Time (minutes)"
                      type="number"
                      control={control}
                      inputProps={{
                        readOnly: !hasUpsertPermissions,
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={4} justifyContent="space-between">
                    <SelectField
                      id="efficiency"
                      name="generalInformation.efficiency"
                      label="Efficiency"
                      control={control}
                      readOnly={!hasUpsertPermissions}
                      errors={errors}
                      options={efficiencyOptions || []}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      id="wipLimit"
                      name="generalInformation.wipLimit"
                      label="WIP Limit"
                      type="number"
                      control={control}
                      inputProps={{
                        readOnly: !hasUpsertPermissions,
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CheckBoxField
                      sx={{
                        checkbox: { padding: 0, margin: 0 },
                        label: { padding: 0, margin: 0 },
                      }}
                      id="generalInformation.isAlwaysAvailable"
                      name="generalInformation.isAlwaysAvailable"
                      label="Always Available"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CheckBoxField
                      sx={{
                        checkbox: { padding: 0, margin: 0 },
                        label: { padding: 0, margin: 0 },
                      }}
                      id="generalInformation.isWorkTeam"
                      name="generalInformation.isWorkTeam"
                      label="Work Team"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {resourceWatch('generalInformation.isWorkTeam') && (
                      <Tooltip
                        title={
                          <p
                            style={{
                              fontSize: '15px',
                              margin: '0',
                              padding: '0',
                            }}
                          >
                            Tasks assigned to this resource will be locked
                            immediately
                          </p>
                        }
                      >
                        <div>
                          <CheckBoxField
                            sx={{
                              checkbox: { padding: 0, margin: 0 },
                              label: { padding: 0, margin: 0 },
                            }}
                            id="generalInformation.lockImmediately"
                            name="generalInformation.lockImmediately"
                            label="Lock Tasks Immediately"
                            control={control}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <InputLabel>Set as Standby</InputLabel>
                    <ButtonGroup>
                      <Button
                        variant={
                          resourceWatch('generalInformation.isStandby') === true
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => {
                          resourceSetValue(
                            'generalInformation.isStandby',
                            true,
                            { shouldDirty: true }
                          );
                        }}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={
                          resourceWatch('generalInformation.isStandby') ===
                          false
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => {
                          resourceSetValue(
                            'generalInformation.isStandby',
                            false,
                            { shouldDirty: true }
                          );
                        }}
                      >
                        No
                      </Button>
                    </ButtonGroup>
                  </Grid>
                </Grid>
                <SectionDivider />
                <Grid container alignItems="center" spacing={2}>
                  <ClaimCapacityBulk control={control} errors={errors} />
                </Grid>
              </Section>
            </Section>
          </div>
        </TabPanel>
      );
    },
    [hasUpsertPermissions, efficiencyOptions, resourceWatch, resourceSetValue]
  );

  const renderSchedules = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <div
          style={{
            padding: '10px 24px',
          }}
        >
          <RadioField
            row
            control={control}
            id={'isAppendValues.isAppendSchedules'}
            defaultValue={'false'}
            buttons={[
              {
                label: 'Append',
                value: 'true',
              },
              {
                label: 'Replace',
                value: 'false',
              },
            ]}
          />
          <SectionDivider />
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <RenderWorkdaySection
                hasUpsertSchedulesPermissions={hasUpsertSchedulesPermissions}
                handleOpenEditSchedule={handleOpenEditSchedule}
                renderAddedEvents={renderAddedEvents(
                  resourceWatch('schedules'),
                  'schedules'
                )}
                openEditSchedule={openEditSchedule}
                handleCloseEditSchedule={handleCloseEditSchedule}
                isEdit={isEdit}
                permissions={permissions}
                resourceSetValue={resourceSetValue}
                resourceWatch={resourceWatch}
              />
            </Grid>
            <Grid item xs={12}>
              <RenderBlockedTimeSection
                resourceCount={resourceCount}
                hasUpsertEventsPermissions={hasUpsertEventsPermissions}
                handleOpenEditEvent={handleOpenEditEvent}
                renderAddedEvents={renderAddedEvents(
                  resourceWatch('events'),
                  'events'
                )}
                openEditEvent={openEditEvent}
                handleCloseEditEvent={handleCloseEditEvent}
                isEdit={isEdit}
                permissions={permissions}
                resourceSetValue={resourceSetValue}
                resourceWatch={resourceWatch}
                resourceSetError={resourceSetError}
              />
            </Grid>
          </Grid>
        </div>
      </TabPanel>
    );
  };

  const renderLocations = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <div
          style={{
            padding: '10px 24px',
          }}
        >
          <RadioField
            row
            control={control}
            id={'isAppendValues.isAppendLocations'}
            defaultValue={'false'}
            buttons={[
              {
                label: 'Append',
                value: 'true',
              },
              {
                label: 'Replace',
                value: 'false',
              },
            ]}
          />
          <SectionDivider />
          <LocationsTabBulk
            control={control}
            errors={errors && errors['locations'] ? errors['locations'] : null}
            permissions={permissions}
            watch={resourceWatch}
            statesOptions={statesOptions}
            resourceSetValue={resourceSetValue}
            resourceErrors={resourceFormErrors}
            trigger={trigger}
          />
        </div>
      </TabPanel>
    );
  };

  const renderTeamOfficesRoles = React.useCallback(
    (
      currentTab: number,
      control: Control<FieldValues>,
      errors: FieldErrors,
      index: number
    ) => {
      return (
        <TabPanel value={currentTab} index={index}>
          <div
            style={{
              padding: '10px 24px',
            }}
          >
            <RadioField
              row
              control={control}
              id={'isAppendValues.isAppendTeamsOfficesRoles'}
              defaultValue={'false'}
              buttons={[
                {
                  label: 'Append',
                  value: 'true',
                },
                {
                  label: 'Replace',
                  value: 'false',
                },
              ]}
            />
            <SectionDivider />
            <TeamsOfficesRolesBulk
              control={control}
              setValues={resourceSetValue}
              errors={
                errors && errors['teamsOfficesRoles']
                  ? errors['teamsOfficesRoles']
                  : null
              }
              permissions={permissions}
              watch={resourceWatch}
              bulkType={resourceGetValues('type')}
              companyOptions={companyOptions}
              getValues={resourceGetValues}
            />
          </div>
        </TabPanel>
      );
    },
    [companyOptions]
  );

  const { fields, remove, append } = useFieldArray({
    control: resourceFormControl,
    name: 'attributes',
  });

  const renderAttributes = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <div
          style={{
            padding: '10px 24px',
          }}
        >
          <RadioField
            row
            control={control}
            id={'isAppendValues.isAppendAttributes'}
            defaultValue={'false'}
            buttons={[
              {
                label: 'Append',
                value: 'true',
              },
              {
                label: 'Replace',
                value: 'false',
              },
            ]}
          />
          <SectionDivider />
          <AttributeAddBulk
            append={append}
            control={control}
            watch={resourceWatch}
            company={company}
          />
          {fields.map((field, index) => {
            return (
              <AttributesBulkForm
                key={`${field.id}`}
                control={control}
                permissions={permissions}
                index={index}
                remove={remove}
                watch={resourceWatch}
                field={field}
                getValues={resourceGetValues}
                errors={errors && errors[index] ? errors[index] : null}
                setValue={resourceSetValue}
                company={company}
              />
            );
          })}
        </div>
      </TabPanel>
    );
  };

  const findMessage = useCallback((errors: any) => {
    const messages = errors.flatMap((error: any) => {
      if (error.message) {
        return error.message;
      }
      const values = Object.values(error);
      return findMessage(values);
    });

    return messages;
  }, []);

  const getMessages = useCallback(
    (errorObject: FieldErrors) => {
      const values = Object.values(errorObject);
      const messsages = findMessage(values);
      return messsages;
    },
    [findMessage]
  );

  const getTabIndex = useCallback((tab: string) => {
    return tabLabels.findIndex(
      (label) => label.toLocaleUpperCase() === tab.toLocaleUpperCase()
    );
  }, []);

  const ErrorList = useCallback(
    ({ errorObject }: { errorObject: FieldErrors }) => {
      const paths = Object.keys(errorObject);
      const messages = getMessages(errorObject);

      const handleClick = (path: number) => {
        setTab(path);
      };

      return (
        <div>
          {messages.map((__: string, index: number) => {
            let currentPath = 4;
            let messageText = '';
            if (paths[index] === 'teamsOfficesRoles') {
              currentPath = getTabIndex('TEAMS, OFFICES & ROLES');
              messageText = 'Validation error in Teams/Offices/Roles';
            } else if (paths[index] === 'claimOwner') {
              currentPath = getTabIndex('GENERAL');
              messageText = 'Validation error in General Information';
            } else if (paths[index] === 'locations') {
              currentPath = getTabIndex('LOCATIONS');
              messageText = 'Validation error in Locations';
            } else {
              return <></>;
            }

            return (
              <Stack direction={'row'} gap={1} alignItems="center">
                <p
                  key={index}
                  style={{
                    padding: 0,
                    margin: 0,
                    color: red[800],
                  }}
                >
                  {messageText}
                </p>
                <Button
                  sx={{ padding: 0 }}
                  onClick={() => handleClick(currentPath)}
                >
                  Go to tab
                </Button>
              </Stack>
            );
          })}
        </div>
      );
    },
    [getMessages, getTabIndex, setTab]
  );

  const renderConfirm = React.useCallback(
    (currentTab: number, index: number) => {
      const changedData = resourceWatch();

      return (
        <TabPanel value={currentTab} index={index}>
          <Stack px={2} py={1}>
            <ErrorList errorObject={resourceFormErrors} />
          </Stack>

          {resourceFormIsDirty ? (
            <Stack
              direction="row"
              flexWrap="wrap"
              rowGap={1}
              columnGap={5}
              px={2}
              py={1}
            >
              {changedData?.generalInformation &&
                !allFieldsUndefined(changedData?.generalInformation) && (
                  <div>
                    <StyledH3>General Information </StyledH3>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderGeneralInformationChanges(
                        changedData.generalInformation
                      )}
                    </Stack>
                  </div>
                )}
              {changedData?.claimOwner &&
                !allFieldsUndefined(changedData?.claimOwner) && (
                  <div>
                    <StyledH3>Claim Owner Capacity </StyledH3>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderClaimOwnerCapacityChanges(changedData.claimOwner)}
                    </Stack>
                  </div>
                )}

              {changedData?.locations &&
                !allFieldsUndefined(changedData?.locations) && (
                  <div>
                    <StyledH3>Locations</StyledH3>
                    <Typography variant="h6">
                      {checkIfAppend(
                        resourceGetValues('isAppendValues.isAppendLocations')
                      )}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderLocationChanges({
                        ...changedData?.locations,
                      } as LocationFormBulk)}
                    </Stack>
                  </div>
                )}

              {changedData?.teamsOfficesRoles &&
                !allFieldsUndefined(changedData?.teamsOfficesRoles) && (
                  <div>
                    <StyledH3>Teams/Office/Roles </StyledH3>
                    <Typography variant="h6">
                      {checkIfAppend(
                        resourceGetValues(
                          'isAppendValues.isAppendTeamsOfficesRoles'
                        )
                      )}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderTeamOfficesRolesChanges(
                        changedData.teamsOfficesRoles,
                        companyOptions
                      )}
                    </Stack>
                  </div>
                )}
              {changedData?.attributes &&
                !allFieldsUndefined(changedData?.attributes) && (
                  <div>
                    <StyledH3>Attributes</StyledH3>
                    <Typography variant="h6">
                      {checkIfAppend(
                        resourceGetValues('isAppendValues.isAppendAttributes')
                      )}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderAttributesChanges(changedData.attributes)}
                    </Stack>
                  </div>
                )}
              {changedData?.schedules && (
                <div>
                  <StyledH3>Schedules</StyledH3>
                  <Typography variant="h6">
                    {checkIfAppend(
                      resourceGetValues('isAppendValues.isAppendSchedules')
                    )}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                    {renderAddedEvents(changedData.schedules, 'schedules')}
                  </Stack>
                </div>
              )}
              {changedData?.events && (
                <div>
                  <StyledH3>Blocked Time</StyledH3>
                  <Typography variant="h6">
                    {checkIfAppend(
                      resourceGetValues('isAppendValues.isAppendSchedules')
                    )}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                    {renderAddedEvents(changedData.events, 'events')}
                  </Stack>
                </div>
              )}
            </Stack>
          ) : (
            <EmptyBulkResourceForm />
          )}
        </TabPanel>
      );
    },
    [
      resourceWatch,
      ErrorList,
      resourceFormErrors,
      resourceFormIsDirty,
      resourceGetValues,
      companyOptions,
      renderAddedEvents,
    ]
  );

  const goToNextTab = () => {
    setTab(currentTab + 1);
  };
  const idsSet = React.useMemo(() => {
    return !isEmpty(resourceGetValues('ids'));
  }, [resourceGetValues('ids')]);

  const disabledSave = React.useMemo(() => {
    if (!isEmpty(resourceFormErrors)) return true;
    if (currentTab !== 5) return true;

    const currentValues = resourceGetValues();
    const checkedValues = {
      ...currentValues,
      exclusionZoneInformation: currentValues?.exclusionZonesInformation,
      type: undefined,
      ids: undefined,
    };
    const allEmpty = allFieldsUndefined(checkedValues);

    return !allEmpty;
  }, [resourceFormIsDirty, idsSet, resourceGetValues(), resourceFormErrors]);

  return (
    <DrawerComponent
      title="Bulk Update Resources"
      onClose={() => {
        navigate(navigateToOnClose);
      }}
      onSave={currentTab === 5 ? handleBulkSave : goToNextTab}
      saveButtonLabel={currentTab === 5 ? 'Save' : 'Next'}
      saveDisabled={upsertBulkStatus === 'loading' || !disabledSave}
      sx={{
        width: '65vw',
        '& .MuiBox-root': {
          padding: 0,
        },
      }}
    >
      <FormProvider {...methods}>
        <Form<ResourceBulkForm>
          formId="resourceForm"
          errors={resourceFormErrors}
          control={resourceFormControl}
          handleSubmit={resourceHandleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <>
                <StyledFieldSet>
                  <Section title="Select Resources By">
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12}>
                        <SelectField
                          id="type"
                          name="type"
                          label="Type"
                          control={resourceFormControl as any}
                          readOnly={!hasUpsertPermissions}
                          errors={
                            errors && errors['type'] ? errors['type'] : null
                          }
                          error={errors['type']?.message}
                          options={[
                            { title: 'Team', value: 'Team' },
                            { title: 'Office', value: 'Office' },
                            { title: 'Resources', value: 'Resources' },
                            { title: 'Group', value: 'Group' },
                          ]}
                          onValueChange={() => resourceSetValue('ids', [])}
                        />
                      </Grid>
                      {currentType &&
                        renderIdField(resourceGetValues('type') || '')}
                    </Grid>
                  </Section>
                </StyledFieldSet>

                <TabContainer
                  tabLabels={tabLabels}
                  setTab={setTab}
                  currentTab={currentTab}
                  disabledTabs={[]}
                  sx={{
                    backGroundStyles: {
                      backgroundColor: '#ffffff',
                      borderBottom: '1px solid #E0E0E0',
                      marginTop: '24px',
                    },
                    tabStyles: {
                      border: '1px solid #E0E0E0',
                    },
                  }}
                />

                <div style={{ width: '100%' }}>
                  <StyledFieldSet disabled={!hasUpsertPermissions}>
                    {renderGeneral(
                      currentTab,
                      control,
                      errors,
                      getTabIndex('GENERAL')
                    )}
                    {renderSchedules(
                      currentTab,
                      control,
                      errors,
                      getTabIndex('SCHEDULES')
                    )}
                    {renderLocations(
                      currentTab,
                      control,
                      errors,
                      getTabIndex('LOCATIONS')
                    )}
                    {renderTeamOfficesRoles(
                      currentTab,
                      control,
                      errors,
                      getTabIndex('TEAMS, OFFICES & ROLES')
                    )}
                    {renderAttributes(
                      currentTab,
                      control,
                      errors,
                      getTabIndex('ATTRIBUTES')
                    )}
                    {renderConfirm(currentTab, getTabIndex('Confirm'))}
                  </StyledFieldSet>
                </div>
              </>
            );
          }}
        />
      </FormProvider>
    </DrawerComponent>
  );
}
